import React, { useContext, useEffect, useState } from 'react';
import { TouchableHighlight, View } from 'react-native';
import DyteText from './DyteText';
import { StyleSheet } from 'react-native';
import { SvgXml } from 'react-native-svg';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { useDyteSelector } from '@dytesdk/react-native-core';
import DyteClient from '@dytesdk/web-core';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  size,
  variant,
  iconPack = defaultIconPack,
  t,
}: {
  meeting: DyteClient;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'button' | 'horizontal';
  iconPack?: IconPack;
  t?: DyteI18n;
}) {
  const canProduceAudioInitialValue = () => {
    const { self, meta, stage } = meeting;
    let _canProduceAudio = self.permissions.canProduceAudio === 'ALLOWED';
    const isWebinar = meta.viewType === 'WEBINAR';
    if (!_canProduceAudio && isWebinar) {
      const canRequestAudio =
        self.permissions.canProduceAudio === 'CAN_REQUEST';
      // If the peer can present or has approved request to present
      if (
        canRequestAudio &&
        (stage.status === 'ON_STAGE' ||
          stage.status === 'ACCEPTED_TO_JOIN_STAGE')
      ) {
        _canProduceAudio = true;
      }
    }
    return _canProduceAudio;
  };
  const audioEnabled = useDyteSelector((m) => m.self.audioEnabled);
  const [canProduceAudio, setCanProduceAudio] = useState(
    canProduceAudioInitialValue()
  );
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  useEffect(() => {
    const updateCanProduceAudio = () => {
      const { self, meta, stage } = meeting;
      let _canProduceAudio = self.permissions.canProduceAudio === 'ALLOWED';
      const isWebinar = meta.viewType === 'WEBINAR';
      if (!_canProduceAudio && isWebinar) {
        const canRequestAudio =
          self.permissions.canProduceAudio === 'CAN_REQUEST';
        // If the peer can present or has approved request to present
        if (
          canRequestAudio &&
          (stage.status === 'ON_STAGE' ||
            stage.status === 'ACCEPTED_TO_JOIN_STAGE')
        ) {
          _canProduceAudio = true;
        }
      }
      setCanProduceAudio(_canProduceAudio);
    };
    updateCanProduceAudio();
    meeting.stage.addListener('stageStatusUpdate', updateCanProduceAudio);

    return () => {
      meeting.stage.removeListener('stageStatusUpdate', updateCanProduceAudio);
    };
  }, [meeting, canProduceAudio]);

  let buttonSize = 25;
  let buttonHorizontal = false;
  const mediaAudioPermission = useDyteSelector(
    (m) => m.self.mediaPermissions.audio
  );
  switch (size) {
    case 'md':
      buttonSize = 50;
      break;
    case 'lg':
      buttonSize = 100;
      break;
    case 'xl':
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      buttonSize = 150;
      break;
  }
  switch (variant) {
    case 'horizontal':
      buttonHorizontal = true;
      break;
  }
  const toggleAudio = async () => {
    if (mediaAudioPermission === 'DENIED') {
      setStates({
        ...storeStates,
        permissionGranted: false,
      });
      return;
    }
    try {
      if (audioEnabled) await meeting.self.disableAudio();
      else await meeting.self.enableAudio();
    } catch (e) {
      console.log('Audio Err: ', e);
    }
  };
  const styles = StyleSheet.create({
    container: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconStyle: {
      height: 56,
      width: 56,
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'row',
    },
    iconWrapper: {
      flex: 1,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
    },
    text: {
      marginLeft: 10,
      fontSize: 11,
      marginTop: 10,
    },
  });

  if (!canProduceAudio) return null;

  return (
    <View style={styles.container}>
      {buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={toggleAudio}
          style={styles.iconStyle}
        >
          {audioEnabled ? (
            <View style={styles.iconWrapper}>
              <SvgXml
                xml={iconPack.mic_on.replace('currentColor', colors.text)}
                width={25}
                height={25}
              />
              <DyteText style={styles.text}>{t('mic_on')}</DyteText>
            </View>
          ) : (
            <View style={styles.iconWrapper}>
              <SvgXml
                xml={iconPack.mic_off.replace('currentColor', colors.danger)}
                width={25}
                height={25}
              />
              <DyteText style={styles.text}>{t('mic_off')}</DyteText>
            </View>
          )}
        </TouchableHighlight>
      )}
      {!buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={toggleAudio}
          style={styles.iconStyle}
        >
          {audioEnabled ? (
            <SvgXml
              xml={iconPack.mic_on.replace('currentColor', colors.text)}
              width={25}
              height={25}
            />
          ) : (
            <SvgXml
              xml={iconPack.mic_off.replace('currentColor', colors.danger)}
              width={25}
              height={25}
            />
          )}
        </TouchableHighlight>
      )}
      {mediaAudioPermission !== 'ACCEPTED' && (
        <View
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            position: 'absolute',
            top: 3,
            right: 3,
          }}
        >
          <SvgXml
            xml={iconPack.warning.replace('currentColor', colors.warning)}
            width={15}
            height={15}
          />
        </View>
      )}
    </View>
  );
}
